import { Colors, Icon, type IconName } from '@amelio/ui-library';

import type { AssessmentMethod } from '@/types/manager-assessment/Evaluation';

import { useAmelioTranslations } from '@/hooks';

interface SkillOptionConfig {
    id: 'all' | 'weakest' | 'old';
    iconName: IconName;
    count: number;
}

interface SkillChooserProps {
    skillCounts: { all: number; weakest: number; old: number };
    selectedSkillOption: string | null;
    selectedMethod: AssessmentMethod | null;
    onSkillOptionSelect: (optionId: 'all' | 'weakest' | 'old') => void;
    onMethodSelect: (method: 'custom' | 'template') => void;
}

export const SkillChooser = ({
    skillCounts,
    selectedSkillOption,
    selectedMethod,
    onSkillOptionSelect,
    onMethodSelect,
}: SkillChooserProps) => {
    const { t } = useAmelioTranslations();

    const skillOptions: SkillOptionConfig[] = [
        { id: 'all', iconName: 'edit-task-short', count: skillCounts.all },
        { id: 'weakest', iconName: 'business-trending-down', count: skillCounts.weakest },
        { id: 'old', iconName: 'business-trending-down', count: skillCounts.old },
    ];

    return (
        <div
            className={'flex flex-col gap-6'}
            data-testid={'skill-chooser'}
        >
            {/* Skill option cards */}
            <div className={'flex flex-col gap-6 sm:flex-row lg:!gap-8'}>
                {skillOptions.map((option) => {
                    const isSelected =
                        selectedSkillOption === option.id && selectedMethod === 'predefined';
                    // No competencies to select for this preset — nothing to choose, so disable it.
                    const isDisabled = option.count === 0;

                    return (
                        <button
                            key={option.id}
                            type={'button'}
                            disabled={isDisabled}
                            onClick={() => onSkillOptionSelect(option.id)}
                            className={`focus-visible:ring-primary-800 flex min-h-[140px] flex-1 flex-col justify-between rounded-2xl border text-left backdrop-blur-[2px] transition-all focus:outline-none focus-visible:ring-2 sm:max-w-[368px] md:min-h-[161px] ${
                                isDisabled
                                    ? 'cursor-not-allowed border-transparent bg-white/60 opacity-50'
                                    : isSelected
                                      ? 'border-primary bg-white/90'
                                      : 'hover:border-primary border-transparent bg-white/90'
                            }`}
                            data-testid={`skill-option-${option.id}`}
                        >
                            <div className={'flex flex-col px-4 pt-4 md:!px-[31px] md:!pt-[22px]'}>
                                <div className={'flex items-start justify-between'}>
                                    <h4
                                        className={
                                            'text-base leading-[25px] font-medium text-gray-900 md:text-[20px]'
                                        }
                                    >
                                        {t(`managerAssessmentSetup.skillOptions.${option.id}`)}
                                    </h4>

                                    <Icon
                                        name={option.iconName}
                                        size={24}
                                        className={'shrink-0 text-gray-900'}
                                    />
                                </div>

                                <p
                                    className={
                                        'my-4 text-xs leading-normal font-medium text-gray-900 md:!my-1 md:!text-[14px]'
                                    }
                                >
                                    {t(`managerAssessmentSetup.skillOptions.${option.id}Desc`)}
                                </p>
                            </div>

                            <div className={'px-4 pb-4 md:!px-[31px] md:!pb-[22px]'}>
                                <span
                                    className={
                                        'leading-6 font-medium text-(--color-primary-600) text-[12]'
                                    }
                                >
                                    {t('managerAssessmentSetup.skillOptions.skillCount', {
                                        count: option.count,
                                    })}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* OR divider */}
            <div className={'relative my-[42px] flex items-center justify-center'}>
                <div className={'bg-muted absolute right-0 left-0 h-px'} />

                <div
                    className={
                        'relative rounded-full bg-(--color-primary-500) px-[21.73px] py-[7.243px]'
                    }
                >
                    <span className={'text-[20px] font-medium text-white'}>
                        {t('managerAssessmentSetup.or')}
                    </span>
                </div>
            </div>

            {/* Alternative methods */}
            <div className={'flex flex-col gap-4 sm:flex-row md:!gap-8'}>
                {/* Custom */}
                <button
                    type={'button'}
                    onClick={() => onMethodSelect('custom')}
                    className={`focus-visible:ring-primary-800 flex min-h-[100px] flex-1 items-start gap-4 rounded-2xl border p-4 text-left backdrop-blur-[2px] transition-all focus:outline-none focus-visible:ring-2 sm:max-w-[572px] md:min-h-[130px] md:gap-6 md:!p-7 ${
                        selectedMethod === 'custom'
                            ? 'border-primary bg-white/90 shadow-[0_2px_6px_0_rgba(45,52,57,0.06)]'
                            : 'hover:border-primary border-transparent bg-white/90'
                    }`}
                    data-testid={'method-custom'}
                >
                    <div
                        className={
                            'bg-base-50 flex size-12 shrink-0 items-center justify-center rounded-full md:size-13'
                        }
                    >
                        <Icon
                            name={'essential-tick-square'}
                            size={28}
                            color={Colors.PRIMARY}
                        />
                    </div>

                    <div className={'flex min-w-0 flex-1 flex-col gap-1'}>
                        <h4 className={'text-subtitle font-medium text-gray-900'}>
                            {t('managerAssessmentSetup.methods.custom')}
                        </h4>

                        <p className={'text-sm font-medium text-gray-900'}>
                            {t('managerAssessmentSetup.methods.customDesc')}
                        </p>
                    </div>
                </button>

                {/* Template */}
                <button
                    type={'button'}
                    onClick={() => onMethodSelect('template')}
                    className={`focus-visible:ring-primary-800 flex min-h-[100px] flex-1 items-start gap-4 rounded-2xl border p-4 text-left backdrop-blur-[2px] transition-all focus:outline-none focus-visible:ring-2 sm:max-w-[572px] md:min-h-[130px] md:gap-6 md:!p-7 ${
                        selectedMethod === 'template'
                            ? 'border-primary bg-white/90 shadow-[0_2px_6px_0_rgba(45,52,57,0.06)]'
                            : 'hover:border-primary border-transparent bg-white/90'
                    }`}
                    data-testid={'method-template'}
                >
                    <div
                        className={
                            'bg-base-50 flex size-12 shrink-0 items-center justify-center rounded-full md:size-13'
                        }
                    >
                        <Icon
                            name={'grid-element-4'}
                            size={28}
                            color={Colors.PRIMARY}
                        />
                    </div>

                    <div className={'flex min-w-0 flex-1 flex-col gap-1'}>
                        <h4 className={'text-subtitle font-medium text-gray-900'}>
                            {t('managerAssessmentSetup.methods.template')}
                        </h4>

                        <p className={'text-sm font-medium text-gray-900'}>
                            {t('managerAssessmentSetup.methods.templateDesc')}
                        </p>
                    </div>
                </button>
            </div>
        </div>
    );
};
